import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { MessageSquare, Sparkles, User } from 'lucide-react';
import Avatar from '@/components/comms/Avatar';
import type { InternalCommsMessage } from '@/domain/types';
import type { Person } from '@/utils/commsHelpers';
import { useBrainStore } from '@/state/brainStore';
import { simTimeShort } from '@/utils/format';

interface Props {
  message: InternalCommsMessage;
  peopleById: Map<string, Person>;
  threadReplyCount?: number;
  onOpenThread?: (messageId: string) => void;
  onReact?: (messageId: string, emoji: string) => void;
  showThreadAffordance?: boolean;
  selfPersonId?: string | null;
  compact?: boolean;
}

const QUICK_EMOJI = ['👍', '🔥', '✅', '❤️', '🙌', '👀'];

// Render the message body with @mentions as gold chips. Recognises tokens of
// the form @firstName-lastName or @id (matched against the people pool).
function renderBody(
  body: string,
  peopleById: Map<string, Person>,
  mentions: string[] | undefined,
): React.ReactNode {
  if (!mentions || mentions.length === 0) return body;
  // Build a regex matching any mentioned id by name fragment (firstName or full).
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  // Walk through body looking for "@<word>" tokens. For each, try to resolve.
  const re = /@([a-zA-Z][\w-]+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body))) {
    const tag = m[1]!;
    const matched = mentions.find((id) => id === tag || peopleById.get(id)?.displayName.toLowerCase().split(/\s+/).includes(tag.toLowerCase()));
    if (matched) {
      if (m.index > cursor) parts.push(body.slice(cursor, m.index));
      const person = peopleById.get(matched);
      parts.push(
        <span
          key={`${m.index}-mention`}
          className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-gold/15 text-gold-bright font-medium"
        >
          @{person?.displayName ?? tag}
        </span>,
      );
      cursor = m.index + m[0].length;
    }
  }
  if (cursor < body.length) parts.push(body.slice(cursor));
  return parts.length > 0 ? <>{parts}</> : body;
}

export default function MessageRow({
  message,
  peopleById,
  threadReplyCount = 0,
  onOpenThread,
  onReact,
  showThreadAffordance = true,
  selfPersonId,
  compact = false,
}: Props) {
  const navigate = useNavigate();
  const author = peopleById.get(message.authorId);
  const customer = useBrainStore((s) =>
    message.customerRefId ? s.customers[message.customerRefId] : undefined,
  );
  const isAutoPing = message.autoWinPing === true;

  return (
    <article
      data-testid={`comms-message-${message.id}`}
      className={clsx(
        'group flex gap-3 px-4 py-2 rounded transition',
        isAutoPing
          ? 'border-l-2 border-gold-bright/70 bg-gold/5'
          : 'hover:bg-ink-soft/40',
        compact ? 'py-1.5' : '',
      )}
    >
      <div className="shrink-0 pt-0.5">
        <Avatar person={author} size="md" />
      </div>
      <div className="flex-1 min-w-0">
        {isAutoPing ? (
          <p className="font-inter text-[9px] uppercase tracking-widest text-gold-bright mb-0.5">
            <Sparkles size={9} className="inline mr-1" />
            Ops event
          </p>
        ) : null}
        <header className="flex items-baseline gap-2 flex-wrap">
          <span className="font-inter text-sm text-cream">
            {author?.displayName ?? message.authorId}
          </span>
          <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
            {simTimeShort(message.timestamp)}
          </span>
        </header>
        <p className="font-serif text-cream-soft leading-relaxed mt-0.5 break-words">
          {renderBody(message.body, peopleById, message.mentions)}
        </p>

        {customer ? (
          <button
            type="button"
            onClick={() => navigate(`/customer/${customer.id}`)}
            className="inline-flex items-center gap-1.5 mt-2 px-2 py-1 rounded-md border border-gold-dim/30 bg-ink-deep hover:border-gold-bright/60 transition text-[11px]"
            data-testid={`comms-customer-chip-${message.id}`}
          >
            <User size={10} className="text-gold-bright" />
            <span className="font-inter text-cream">
              {customer.firstName} {customer.lastName}
            </span>
            <span className="font-inter text-[9px] uppercase tracking-widest text-cream-faint">
              {customer.primaryStudioId.replace('-', ' ')}
            </span>
          </button>
        ) : null}

        {message.attachments && message.attachments.length > 0 ? (
          <div className="mt-2 flex gap-2 flex-wrap">
            {message.attachments.map((att, i) =>
              att.kind === 'image' ? (
                <img
                  key={i}
                  src={att.url}
                  alt={att.label ?? 'attachment'}
                  loading="lazy"
                  className="h-24 w-24 object-cover rounded-md border border-gold-dim/30"
                />
              ) : (
                <a
                  key={i}
                  href={att.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] text-gold-bright underline"
                >
                  {att.label ?? att.url}
                </a>
              ),
            )}
          </div>
        ) : null}

        {message.reactions && message.reactions.length > 0 ? (
          <div className="mt-2 flex gap-1 flex-wrap">
            {Object.entries(
              message.reactions.reduce<Record<string, number>>((acc, r) => {
                acc[r.emoji] = (acc[r.emoji] ?? 0) + 1;
                return acc;
              }, {}),
            ).map(([emoji, count]) => (
              <button
                key={emoji}
                type="button"
                onClick={() => onReact?.(message.id, emoji)}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-ink-soft border border-gold-dim/30 hover:border-gold-bright/60 text-[11px]"
              >
                <span>{emoji}</span>
                <span className="font-mono text-cream-soft">{count}</span>
              </button>
            ))}
          </div>
        ) : null}

        {showThreadAffordance && threadReplyCount > 0 ? (
          <button
            type="button"
            onClick={() => onOpenThread?.(message.id)}
            className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-gold-bright hover:text-gold transition"
            data-testid={`comms-thread-toggle-${message.id}`}
          >
            <MessageSquare size={10} />
            {threadReplyCount} {threadReplyCount === 1 ? 'reply' : 'replies'}
          </button>
        ) : null}
      </div>

      {/* Hover reaction picker */}
      {onReact ? (
        <div className="opacity-0 group-hover:opacity-100 transition flex gap-0.5 self-start mt-1">
          {QUICK_EMOJI.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => onReact(message.id, e)}
              className="text-xs hover:scale-125 transition"
              title={`React ${e}`}
            >
              {e}
            </button>
          ))}
          {showThreadAffordance && onOpenThread ? (
            <button
              type="button"
              onClick={() => onOpenThread(message.id)}
              className="text-xs hover:text-gold-bright text-cream-soft ml-1"
              title="Reply in thread"
            >
              <MessageSquare size={11} />
            </button>
          ) : null}
        </div>
      ) : null}
      {/* Avoid unused-var lint */}
      <span className="hidden">{selfPersonId ?? ''}</span>
    </article>
  );
}
